import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const field =
  "w-full px-4 py-3 rounded-xl bg-black/40 border border-amber-500/20 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/60";
const label = "block text-xs uppercase tracking-widest font-mono-accent text-amber-400/70 mb-2";

const Section = ({ title, children }) => (
  <div className="glass rounded-2xl p-6 mb-6">
    <h3 className="font-serif text-xl text-amber-100 mb-5">{title}</h3>
    <div className="space-y-4">{children}</div>
  </div>
);

export const ContentEditor = () => {
  const { refresh } = useSite();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then(({ data }) => setC(data)).catch(() => {});
  }, []);

  if (!c) return <div className="text-slate-500">Chargement…</div>;

  const set = (path, value) => {
    setC((prev) => {
      const next = structuredClone(prev);
      let obj = next;
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", { data: c });
      await refresh();
      toast.success("Contenu enregistré");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-content-editor">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl text-amber-50">Éditeur de contenu</h1>
          <p className="text-slate-400">Modifiez les textes affichés sur le site.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-save-content-button" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-300 to-yellow-600 text-[#08080A] font-medium hover:scale-[1.02] transition-transform disabled:opacity-60">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <><Save size={16} /> Enregistrer</>}
        </button>
      </div>

      <Section title="Section Héro">
        <div><label className={label}>Sur-titre</label><input className={field} value={c.hero.overline} onChange={(e) => set(["hero", "overline"], e.target.value)} /></div>
        <div><label className={label}>Titre</label><input className={field} value={c.hero.title} onChange={(e) => set(["hero", "title"], e.target.value)} /></div>
        <div><label className={label}>Sous-titre</label><textarea rows={3} className={field} value={c.hero.subtitle} onChange={(e) => set(["hero", "subtitle"], e.target.value)} /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={label}>Bouton principal</label><input className={field} value={c.hero.cta_primary} onChange={(e) => set(["hero", "cta_primary"], e.target.value)} /></div>
          <div><label className={label}>Bouton secondaire</label><input className={field} value={c.hero.cta_secondary} onChange={(e) => set(["hero", "cta_secondary"], e.target.value)} /></div>
        </div>
      </Section>

      <Section title="Trois voies">
        {c.paths.map((p, i) => (
          <div key={i} className="grid sm:grid-cols-3 gap-4 border-b border-amber-500/10 pb-4 last:border-0">
            <input className={field} value={p.title} onChange={(e) => set(["paths", i, "title"], e.target.value)} placeholder="Titre" />
            <textarea rows={2} className={`${field} sm:col-span-2`} value={p.description} onChange={(e) => set(["paths", i, "description"], e.target.value)} placeholder="Description" />
          </div>
        ))}
      </Section>

      <Section title="Chemin vers la maîtrise">
        {c.mastery.map((m, i) => (
          <div key={i} className="grid sm:grid-cols-3 gap-4 border-b border-amber-500/10 pb-4 last:border-0">
            <div className="flex gap-2">
              <input className={`${field} w-16`} value={m.step} onChange={(e) => set(["mastery", i, "step"], e.target.value)} />
              <input className={field} value={m.title} onChange={(e) => set(["mastery", i, "title"], e.target.value)} placeholder="Titre" />
            </div>
            <textarea rows={2} className={`${field} sm:col-span-2`} value={m.description} onChange={(e) => set(["mastery", i, "description"], e.target.value)} placeholder="Description" />
          </div>
        ))}
      </Section>

      <Section title="Le Cercle">
        <div><label className={label}>Titre</label><input className={field} value={c.cercle.title} onChange={(e) => set(["cercle", "title"], e.target.value)} /></div>
        {c.cercle.benefits.map((b, i) => (
          <input key={i} className={field} value={b} onChange={(e) => set(["cercle", "benefits", i], e.target.value)} placeholder={`Avantage ${i + 1}`} />
        ))}
      </Section>

      <Section title="Chaîne YouTube">
        <div><label className={label}>Titre</label><input className={field} value={c.youtube.title} onChange={(e) => set(["youtube", "title"], e.target.value)} /></div>
        <div><label className={label}>Sous-titre</label><input className={field} value={c.youtube.subtitle} onChange={(e) => set(["youtube", "subtitle"], e.target.value)} /></div>
        <div><label className={label}>Lien de la chaîne</label><input className={field} value={c.youtube.channel_url} onChange={(e) => set(["youtube", "channel_url"], e.target.value)} placeholder="https://youtube.com/@..." /></div>
      </Section>

      <Section title="Coordonnées">
        <div><label className={label}>Numéro WhatsApp (format international sans +)</label><input className={field} value={c.whatsapp} onChange={(e) => set(["whatsapp"], e.target.value)} placeholder="33757829980" /></div>
        <div><label className={label}>Email de contact</label><input className={field} value={c.contact_email || ""} onChange={(e) => set(["contact_email"], e.target.value)} /></div>
      </Section>
    </div>
  );
};
